/**
 * Children & Devices page.
 *
 * - List of children with paired devices per child
 * - "Add child" form (auto-creates family on first add — same flow as App.tsx home)
 * - "Pair device" dialog: generates a 6-digit code with 15-min expiry +
 *   `kidzio://pair?code=…` deep link, ready to read to the child or scan.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Pencil, Plus, Smartphone, Copy, QrCode, Users, Trash2, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from './EmptyState';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usersApi, type Child, type ChildCreateInput, type PairingCode } from '@/api/users';
import { PageHeader } from './PageHeader';
import { EditChildDialog } from './EditChildDialog';
import { AgeProfilePanel } from './AgeProfilePanel';
import { ChildAvatar } from '@/components/ChildAvatar';

const ChildSchema = z.object({
  name: z.string().trim().min(1, 'pages.children.form_name_required').max(120),
  birth_year: z
    .number()
    .int()
    .min(2000)
    .max(2030)
    .optional()
    .or(z.nan().transform(() => undefined)),
  color: z
    .string()
    .regex(/^#?[0-9A-Fa-f]{6}$/, 'pages.children.form_color_invalid')
    .optional(),
});
type ChildValues = z.infer<typeof ChildSchema>;

function formatRelative(iso: string | null, t: TFunction): string {
  if (!iso) return t('pages.children.rel_never');
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return t('pages.children.rel_just_now');
  if (min < 60) return t('pages.children.rel_min', { n: min });
  const hr = Math.round(min / 60);
  if (hr < 24) return t('pages.children.rel_hour', { n: hr });
  return t('pages.children.rel_day', { n: Math.round(hr / 24) });
}

export function ChildrenPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const children = useQuery({
    queryKey: ['children'],
    queryFn: () => usersApi.listChildren(),
    retry: false,
  });

  const childIds = (children.data ?? []).map((c) => c.id);
  const deviceQueries = useQueries({
    queries: childIds.map((id) => ({
      queryKey: ['devices', id],
      queryFn: () => usersApi.listDevices(id),
      staleTime: 30_000,
    })),
  });

  const [pairing, setPairing] = useState<{ child: Child; code: PairingCode } | null>(null);
  const [editing, setEditing] = useState<Child | null>(null);
  // Device pending unpair confirmation. Stored as the full row so the
  // dialog can show "Unpair Vika's Phone (android)" without re-querying.
  const [unpairTarget, setUnpairTarget] = useState<{
    childId: string;
    childName: string;
    deviceId: string;
    deviceLabel: string;
  } | null>(null);

  const createChild = useMutation({
    mutationFn: async (input: ChildCreateInput) => {
      try {
        return await usersApi.createChild(input);
      } catch (err) {
        if (err instanceof Error && err.message.includes('no_family')) {
          await usersApi.createFamily('My Family');
          return await usersApi.createChild(input);
        }
        throw err;
      }
    },
    onSuccess: (created) => {
      toast.success(t('pages.children.toast_added', { name: created.name }));
      void queryClient.invalidateQueries({ queryKey: ['children'] });
    },
    onError: () => toast.error(t('pages.children.toast_add_failed')),
  });

  const generateCode = useMutation({
    mutationFn: (childId: string) => usersApi.generatePairing(childId),
    onSuccess: (code, childId) => {
      const child = (children.data ?? []).find((c) => c.id === childId);
      if (child) setPairing({ child, code });
    },
    onError: () => toast.error(t('pages.children.toast_code_failed')),
  });

  const unpair = useMutation({
    mutationFn: ({ childId, deviceId }: { childId: string; deviceId: string }) =>
      usersApi.unpairDevice(childId, deviceId),
    onSuccess: (_data, { childId }) => {
      toast.success(t('pages.children.toast_unpaired'));
      setUnpairTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['devices', childId] });
    },
    onError: () => toast.error(t('pages.children.toast_unpair_failed')),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChildValues>({
    resolver: zodResolver(ChildSchema),
    defaultValues: { name: '', color: '#FF9EC4' },
  });

  const onAddChild = async (v: ChildValues) => {
    await createChild.mutateAsync({
      name: v.name,
      birth_year: v.birth_year,
      color: v.color && !v.color.startsWith('#') ? `#${v.color}` : v.color,
    });
    reset({ name: '', color: '#FF9EC4' });
  };

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t('pages.children.title')}
        icon={Users}
        tint="coral"
        description={t('pages.children.description')}
      />

      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_22rem]">
        <section className="space-y-4">
          {children.isPending && (
            <Card>
              <CardContent className="space-y-3 p-6">
                <Skeleton className="h-12 w-full rounded-md" />
                <Skeleton className="h-12 w-full rounded-md" />
              </CardContent>
            </Card>
          )}
          {children.data?.length === 0 && (
            <Card>
              <CardContent className="p-0">
                <EmptyState
                  icon={Users}
                  tint="coral"
                  title={t('pages.children.empty_title')}
                  description={t('pages.children.empty_description')}
                />
              </CardContent>
            </Card>
          )}
          {(children.data ?? []).map((child, i) => {
            const devices = deviceQueries[i]?.data ?? [];
            return (
              <Card key={child.id}>
                <CardHeader className="flex flex-row items-center gap-3 space-y-0">
                  <ChildAvatar
                    name={child.name}
                    color={child.color}
                    avatarUrl={child.avatar_url}
                  />
                  <div className="flex-1 min-w-0">
                    <CardTitle className="flex items-center gap-2 text-base">
                      {child.name}
                      {child.age_group && (
                        <span className="rounded-full bg-[color:var(--kz-color-surface-sunken)] px-2 py-0.5 text-[11px] font-medium text-[color:var(--kz-color-brand-primary)]">
                          {child.age_group}
                        </span>
                      )}
                    </CardTitle>
                    <CardDescription>
                      {child.birth_year
                        ? t('pages.children.born_year', { year: child.birth_year })
                        : t('pages.children.birth_year_unset')}
                      {' · '}
                      {t('pages.children.devices', { count: devices.length })}
                    </CardDescription>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(child)}
                    aria-label={t('pages.children.edit_aria', { name: child.name })}
                  >
                    <Pencil className="size-4" />
                    {t('pages.children.edit')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => generateCode.mutate(child.id)}
                    disabled={generateCode.isPending}
                  >
                    <QrCode className="size-4" />
                    {t('pages.children.pair_device')}
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  <AgeProfilePanel child={child} />
                  {devices.length === 0 ? (
                    <p className="rounded-md border border-dashed border-[color:var(--kz-color-border-subtle)] p-3 text-xs text-[color:var(--kz-color-ink-muted)]">
                      {t('pages.children.no_devices_hint')}
                    </p>
                  ) : (
                    <ul className="grid gap-2">
                      {devices.map((d) => (
                        <li
                          key={d.id}
                          className="flex items-center gap-3 rounded-md border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-card)] p-3"
                        >
                          <Smartphone className="size-4 text-[color:var(--kz-color-ink-muted)]" />
                          <div className="flex-1 min-w-0">
                            <p className="truncate text-sm font-medium">
                              {d.label ?? d.model ?? d.platform}
                            </p>
                            <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
                              {t('pages.children.last_seen', { platform: d.platform, relative: formatRelative(d.last_seen_at, t) })}
                            </p>
                          </div>
                          {d.disabled && (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                              {t('pages.children.disabled')}
                            </span>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={t('pages.children.unpair_aria', { label: d.label ?? d.model ?? d.platform })}
                            title={t('pages.children.unpair_title')}
                            onClick={() =>
                              setUnpairTarget({
                                childId: child.id,
                                childName: child.name,
                                deviceId: d.id,
                                deviceLabel: d.label ?? d.model ?? d.platform,
                              })
                            }
                            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </section>

        <aside>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Plus className="size-4" />
                {t('pages.children.add_title')}
              </CardTitle>
              <CardDescription>{t('pages.children.add_description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onAddChild)} noValidate className="grid gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="child-name">{t('pages.children.form_name')}</Label>
                  <Input
                    id="child-name"
                    placeholder={t('pages.children.form_name_placeholder')}
                    aria-invalid={!!errors.name}
                    {...register('name')}
                  />
                  {errors.name?.message && (
                    <p className="text-xs text-[color:var(--kz-color-status-danger)]">
                      {t(errors.name.message)}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1.5">
                    <Label htmlFor="child-birth">{t('pages.children.form_birth_year')}</Label>
                    <Input
                      id="child-birth"
                      type="number"
                      min={2000}
                      max={2030}
                      placeholder="2017"
                      {...register('birth_year', { valueAsNumber: true })}
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="child-color">{t('pages.children.form_color')}</Label>
                    <Input
                      id="child-color"
                      type="color"
                      className="h-10 cursor-pointer p-1"
                      {...register('color')}
                    />
                  </div>
                </div>
                <Button type="submit" disabled={createChild.isPending} className="mt-1">
                  {createChild.isPending ? t('pages.children.adding') : t('pages.children.add_child')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </aside>
      </div>

      <PairingDialog
        pairing={pairing}
        onClose={() => {
          setPairing(null);
          // Refresh device list — pair flow ends on the child device, but the
          // parent might pair-then-refresh during a demo.
          void queryClient.invalidateQueries({ queryKey: ['devices'] });
        }}
      />

      <EditChildDialog
        child={editing}
        onClose={() => setEditing(null)}
      />

      <Dialog open={unpairTarget !== null} onOpenChange={(open) => !open && setUnpairTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('pages.children.unpair_confirm_title', { label: unpairTarget?.deviceLabel ?? '' })}
            </DialogTitle>
            <DialogDescription>
              {t('pages.children.unpair_confirm_body', { name: unpairTarget?.childName ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setUnpairTarget(null)} disabled={unpair.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (unpairTarget) {
                  unpair.mutate({ childId: unpairTarget.childId, deviceId: unpairTarget.deviceId });
                }
              }}
              disabled={unpair.isPending}
            >
              {unpair.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t('pages.children.unpairing')}
                </>
              ) : (
                <>
                  <Trash2 className="size-4" />
                  {t('pages.children.unpair_title')}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PairingDialog({
  pairing,
  onClose,
}: {
  pairing: { child: Child; code: PairingCode } | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  if (!pairing) return null;
  const expiresAt = new Date(pairing.code.expires_at);
  const minutesLeft = Math.max(0, Math.round((expiresAt.getTime() - Date.now()) / 60_000));
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('pages.children.pair_dialog_title', { name: pairing.child.name })}
          </DialogTitle>
          <DialogDescription>{t('pages.children.pair_dialog_description')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid place-items-center rounded-lg border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-sunken)] p-6">
            <span className="font-mono text-4xl font-semibold tracking-[0.4em]">
              {pairing.code.code}
            </span>
            <p className="mt-2 text-xs text-[color:var(--kz-color-ink-muted)]">
              {t('pages.children.pair_expires_in', { minutes: minutesLeft })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input value={pairing.code.deep_link} readOnly className="font-mono text-xs" />
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                void navigator.clipboard.writeText(pairing.code.deep_link);
                toast.success(t('pages.children.link_copied'));
              }}
              aria-label={t('pages.children.copy_deep_link')}
            >
              <Copy className="size-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
